import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { cloneDeep, isString } from "lodash";
import { Option } from "uikit";
import { useTranslation } from "react-i18next";
import { LatLngLiteral } from "leaflet";
import { useDebounce, useDebouncedCallback } from "use-debounce";

import { Map } from "../../../../../../services";
// import { useMapSearch } from "../../../../../../hooks";
import { MapSearch } from "../../../../../Orders";
import { Popup } from "../../../../../common";
import PointOrderModalTableBase from "../..";
import { StyledInput, StyledPopup } from "../styled";
import { OverlayStreet } from "../Overlays";
import {
	HandleEventsProps,
	useGetCoordinatesForStreet,
	useHandleEvents,
	ValueAccessors,
} from "../../hooks";
import cityToString from "../utils/cityToString";
import useTaxiServiceMapName from "../../../../../../hooks/useTaxiServiceMapName";
import { cleanSettlementText } from "../utils/normolize";
import { useMapSearchStreet } from "../../hooks/useMapSearchStreet";

const StreetBase: React.FC<StreetBase.Props> = ({
	rowData,
	rowIndex,
	dataKey,
	colIndex,
	styles,
	offset,
	customerId,
	orderId,
	setData,
	settlement,
	settlementCoordinates,
	street,
	addStreet,
	height,
	handleEventsOptions,
	containerId,
	searchTypes = [],
	taxiServiceId,
	addSettlement,
	addSettlementCoordinates,
	countRow,
	setReloadCell,
	reloadCell,
	setLoadingChangePoint,
}) => {
	const { t } = useTranslation();
	const taxiServiceName = useTaxiServiceMapName(taxiServiceId);

	const { get: getStreetCoordinates } = useGetCoordinatesForStreet();

	const {
		response,
		updateOptions,
		loading,
		reset: resetSearchStreet,
	} = useMapSearchStreet();

	const idCel = useMemo(() => rowData.raw.id, [rowData.raw.id]);
	const isLastRow = useMemo(() => idCel === countRow, [countRow, idCel]);

	const {
		language,
		inputValue,
		query,
		open,
		onFocusToInput,
		onChangeInputValue,
		onEnterToInput,
		onExistFromInput,
		onClickToInput,
		setColIndex,
		setDataKey,
		setInputId,
		setRowId,
		setRowIndex,
		reset,
		onBlurCaptureToInput,
		setInputValue,
		allowType,
		setAllowType,
		setOpen,
		debounceClose,
		nextFocus,
		setLoading,
		inputRequestDelayMs,
		setSave,
		nextElementById,
		valueObjectRef,
	} = useHandleEvents(
		{
			idCel,
			isLastRow,
		},
		handleEventsOptions,
	);

	const { pointOnMapFrom, pointOnMapTo, pointOnMap } = useMemo(
		() => ({
			pointOnMapFrom: t("no_address_point_from") ?? "",
			pointOnMapTo: t("no_address_point_to") ?? "",
			pointOnMap: t("point_on_map") ?? "",
		}),
		[t],
	);

	const [isNewInput, setIsNewInput] = useState<boolean>(false);
	const [isSelect, setIsSelect] = useState<boolean>(false);
	const [isQuery, setIsQuery] = useState<boolean>(false);
	const [activeId, setActiveOption] = useState<number>(-1);
	// const [result, setResult] = useState<MapSearch.Result>({
	// 	loading: true,
	// });

	// Reset the local input only when the point CONTENT changes, not the
	// identity of `rowData.raw`: parents (addTableRows -> cloneDeep,
	// PointsTable -> formatTableOrderPoint) recreate raw on every own
	// re-render (WS updates, cost recalculation, companies in redux), and
	// depending on the object wiped the letter that was just typed.
	useEffect(() => {
		if (!rowData.raw.type) {
			reset();
			// setResult({ loading: true });
			resetSearchStreet();
			setIsNewInput(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		reset,
		orderId,
		rowData.raw.type,
		rowData.raw.settlement,
		rowData.raw.street,
		rowData.raw.name,
	]);

	useEffect(() => {
		if (isString(query) && allowType === "reset") {
			// setResult({ loading: true });
			resetSearchStreet();
			addStreet(undefined, idCel);
			setAllowType(null);
			setIsNewInput(false);

			return;
		}

		if (
			cleanSettlementText(settlement?.settlement) &&
			cleanSettlementText(settlement?.settlement) !==
				cleanSettlementText(rowData.raw.settlement)
		) {
			rowData.raw.saveType = "street";
			if (taxiServiceName !== "mapmd") {
				setInputValue(null);
				setIsNewInput(false);
			}
			if (
				taxiServiceName === "mapmd" &&
				cleanSettlementText(settlement?.settlement) !==
					cleanSettlementText(street?.settlement)
			) {
				// setResult({ loading: true });
				resetSearchStreet();
				addStreet(undefined, idCel);
				setAllowType(null);
			} else {
				// setResult({ loading: true });
				resetSearchStreet();
				addStreet(undefined, idCel);
				setAllowType(null);
			}
			return;
		}
		// if (!street && !allowType && !rowData.raw.saveType && !result.loading) {
		// 	 setResult({ loading: true });

		// }
		if (!street && !allowType && !rowData.raw.saveType && !loading) {
			resetSearchStreet();
		}
		if (
			rowData.raw.saveType === "house" ||
			rowData.raw.saveType === "entrance"
		) {
			// After the first letter `allowType` is still null
			// (onChangeInputValue looks at the previous inputValue), so
			// without checking for live input this branch wiped the first
			// typed letter.
			if (!allowType && valueObjectRef.current.inputValue === null) {
				rowData.raw.saveType = "street";
				setInputValue(null);
				setIsNewInput(false);
			}
		}
	}, [
		resetSearchStreet,
		addStreet,
		allowType,
		idCel,
		query,
		// result.loading,
		loading,
		rowData.raw,
		setAllowType,
		setInputValue,
		settlement?.settlement,
		street,
		taxiServiceName,
		valueObjectRef,
	]);

	const querySearch = useMemo(() => {
		if (allowType === "query" && isString(query) && query?.length >= 0) {
			setAllowType("reset");
		}
		return query || "";
	}, [allowType, query, setAllowType]);

	useEffect(() => {
		setColIndex(`${colIndex}`);
		setDataKey(dataKey);
		setInputId(`${idCel + dataKey}`);
		setRowId(idCel);
		setRowIndex(`${rowIndex}`);
		setIsNewInput(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const streetShow = useCallback(
		(data: {
			district?: string;
			streetType?: string;
			street?: string;
			customStreet?: string;
			house?: string;
			name?: string;
			title?: string;
			type: PointOrderModalTableBase.OrderModal["raw"]["type"];
			saveType?: PointOrderModalTableBase.OrderModal["raw"]["type"];
			isPlaceholder?: boolean;
			save?: boolean;
			baseSave?: boolean;
		}) => {
			if (!data.type) return "";

			const typeStreet = data.streetType || "";
			const street = data.street || data.customStreet || "";
			const showStreet = `${street} ${typeStreet}`.trim();

			if (
				(data.name && data.type !== "street") ||
				(data.title && data.type !== "street")
			) {
				const name = data.name || data.title || "";
				const value = `${name} ${
					showStreet && pointOnMap !== showStreet
						? `(${showStreet})`
						: ""
				}`.trim();
				return value;
			}

			const isPointOnMap: boolean[] = [
				data.saveType === "settlement" &&
					showStreet === pointOnMap &&
					data.type === "address",
				data.saveType === "city" &&
					!showStreet &&
					data.type === "address",
				data.saveType === "settlement" &&
					showStreet === pointOnMap &&
					data.type === "settlement",
				data.saveType === "city" &&
					showStreet === pointOnMap &&
					data.type === "settlement",
				!!data.district && showStreet === pointOnMap,
				!!data.district && !showStreet,
				showStreet === pointOnMap,
				!!data?.baseSave && !data.save,
			];

			if (isPointOnMap.includes(true) && data?.isPlaceholder) {
				return "isPlaceholder";
			}

			return showStreet || pointOnMap;
		},
		[pointOnMap],
	);

	const point = useMemo(() => settlementCoordinates, [settlementCoordinates]);

	const updatedData = useCallback(
		(params: Map.Search.Object) => {
			if (
				cleanSettlementText(settlement?.settlement) &&
				cleanSettlementText(settlement?.settlement) !==
					cleanSettlementText(params.settlement)
			) {
				addSettlement(params, idCel);

				if (
					params.coordinates?.lat !== 0 &&
					params.coordinates?.lng !== 0
				) {
					addSettlementCoordinates(params.coordinates, idCel);
				}
			}
			// OSM / address: nextFocus() (~10ms) ловить той самий Enter —
			// keyup уже в полі будинку, номер не обирається, червона рамка.
			window.setTimeout(() => {
				const el = document.getElementById(
					`${idCel}house`,
				) as HTMLInputElement | null;
				if (!el) return;
				el.focus();
				const len = el.value.length;
				if (len > 0) {
					try {
						el.setSelectionRange(len, len);
					} catch {
						/* ignore */
					}
				}
			}, 50);
			addStreet(params, idCel);

			const oldCoordinates = `${rowData.raw?.coordinates?.lat}${rowData.raw?.coordinates?.lng}`;
			const city = cityToString(params);
			const street = streetShow(params);
			const newData = structuredClone(rowData);

			const optionsType: Map.Search.Type[] = [
				"favorite",
				"object",
				"localObject",
			];
			// base
			newData.city = city;
			newData.street = street;
			newData.name = params.name || "";
			newData.customHouse = params.customHouse || "";
			newData.customStreet = params.customStreet || "";
			newData.title = params.title || "";

			newData.save = true;
			newData.saveType = "street";
			newData.raw.saveType = "street";
			newData.raw.save = !!rowData.raw.type;

			if (!params.houses?.length) newData.raw.save = true;
			else newData.raw.save = false;

			const paramsHouse = String(
				params.house || params.number || "",
			).trim();

			if (
				newData.house ||
				newData.raw.house ||
				newData.raw.customHouse ||
				newData.entrance ||
				newData.raw.entrance ||
				(params.searchType === "osm_nominatim" && paramsHouse) ||
				(params.type === "address" && paramsHouse)
			)
				newData.raw.save = true;
			if (optionsType.includes(params.type)) newData.raw.save = true;

			newData.type = params.type;
			newData.coordinates = params.coordinates;
			newData.house = params.house || params.number || "";
			(newData as Map.Search.Object).searchType = params.searchType;
			// raw
			newData.raw.type = params.type;
			(newData.raw as { searchType?: string }).searchType =
				params.searchType;
			newData.raw.coordinates = params.coordinates;
			newData.raw.country = params.country;
			newData.raw.countryCode = params.countryCode;

			newData.raw.sector = params.sector || newData.sector || undefined;

			newData.raw.region = params.region || "";
			newData.raw.district = params.district || "";

			newData.raw.settlement = params.settlement || "";
			newData.raw.settlementType = params.settlementType || "";

			newData.raw.name = params.name || params.title || "";
			newData.raw.customHouse = params.customHouse || "";
			newData.raw.customStreet = params.customStreet || "";

			newData.raw.street = params.street || params.customStreet || "";
			newData.raw.streetType = params.streetType || "";
			newData.raw.house =
				params.house || params.number || params.customHouse || "";

			// clear
			const entrance = optionsType.includes(params.type)
				? params.entrance
				: "";
			newData.entrance = entrance || "";
			newData.polygon = "";
			newData.flat = "";
			// clear raw
			newData.raw.entrance = entrance || "";
			newData.raw.flat = "";

			const newCoordinates = `${params.coordinates?.lat}${params.coordinates?.lng}`;
			const isValidation =
				cleanSettlementText(rowData.raw.settlement) ===
					cleanSettlementText(newData.raw.settlement) &&
				cleanSettlementText(rowData.raw.street) ===
					cleanSettlementText(newData.raw.street) &&
				rowData.raw.streetType === newData.raw.streetType &&
				cleanSettlementText(rowData.raw.name) ===
					cleanSettlementText(newData.raw.name) &&
				oldCoordinates === newCoordinates;

			// reset
			if (isValidation) {
				setOpen(false);
				debounceClose();
				setInputValue(street);
				// setResult({ loading: true });
				resetSearchStreet();
				return;
			}

			setData(newData);
			setTimeout(() => {
				setReloadCell(`${idCel}street`);
			}, 10);
			reset();
			debounceClose();
			setInputValue(street);
			// setResult({ loading: true });
			resetSearchStreet();
		},
		[
			resetSearchStreet,
			addSettlement,
			addSettlementCoordinates,
			addStreet,
			debounceClose,
			idCel,
			reset,
			rowData,
			setData,
			setInputValue,
			setOpen,
			setReloadCell,
			settlement?.settlement,
			streetShow,
		],
	);

	const updatedDataAsCity = useCallback(
		(str: string) => {
			const newData = cloneDeep(rowData);

			const street = str || pointOnMap;

			const baseKievCenter: LatLngLiteral = {
				lat: 50.450112956330585,
				lng: 30.523430998792342,
			};
			const settlementRaw = newData.raw.settlement || "";
			// eslint-disable-next-line regex/invalid
			const isKiev = ["киев", "київ", "kyiv"].includes(
				settlementRaw.toLowerCase(),
			);

			const coordinates = isKiev
				? baseKievCenter
				: point || newData.coordinates || newData.raw.coordinates;
			// base
			newData.coordinates = coordinates;
			newData.save = true;
			newData.saveType = "city";
			newData.raw.save = true;
			newData.raw.saveType = "city";
			// clear
			newData.entrance = "";
			newData.house = "";
			newData.polygon = "";
			newData.flat = "";
			newData.street = street;
			// clear raw
			newData.raw.customHouse = "";
			newData.raw.customStreet = "";
			newData.raw.entrance = "";
			newData.raw.flat = "";
			newData.raw.house = "";
			newData.raw.name = "";
			newData.raw.street = street;
			newData.raw.streetType = "";
			newData.raw.sector = undefined;
			newData.raw.coordinates = coordinates;
			// reset
			setIsSelect(true);
			setLoading(false);
			setTimeout(() => {
				setReloadCell(`${idCel}street`);
			}, 10);
			const item: Map.Search.Object = {
				...newData.raw,
				id: "query",
				type: "object",
				language,
				bbox: [],
				geometry: settlement?.geometry || {
					type: "Point",
					coordinates,
				},
				coordinates,
				street,
				country: "",
				countryCode: "",
				entrance: "",
				house: "",
				customHouse: "",
				customStreet: "",
				name: "",
				sector: undefined,
				region: "",
				streetType: "",
				district: "",
				houses: [],
			};
			setLoadingChangePoint?.(true);
			addStreet(item, idCel);
			setData(newData);
			const nextElement = isLastRow
				? "order-save-id"
				: `${idCel + 1}street`;
			nextElementById(nextElement);
			setIsNewInput(false);
			// setResult({ loading: true });
			resetSearchStreet();
			reset();
		},
		[
			resetSearchStreet,
			rowData,
			pointOnMap,
			point,
			setLoading,
			language,
			settlement?.geometry,
			setLoadingChangePoint,
			addStreet,
			idCel,
			setData,
			isLastRow,
			nextElementById,
			reset,
			setReloadCell,
		],
	);

	const updatedDataClear = useCallback(() => {
		const newData = cloneDeep(rowData);

		if (!newData.street) return;
		// base
		newData.save = true;
		newData.saveType = "city";
		newData.raw.save = true;
		newData.raw.saveType = "city";
		// clear
		newData.entrance = "";
		newData.house = "";
		newData.polygon = "";
		newData.flat = "";
		newData.street = "";
		// clear raw
		newData.raw.customHouse = "";
		newData.raw.customStreet = "";
		newData.raw.entrance = "";
		newData.raw.flat = "";
		newData.raw.house = "";
		newData.raw.name = "";
		newData.raw.street = "";
		newData.raw.streetType = "";
		newData.raw.sector = undefined;

		// reset

		newData.coordinates = { lat: 0, lng: 0 };
		newData.raw.coordinates = { lat: 0, lng: 0 };

		setLoadingChangePoint?.(true);

		setTimeout(() => {
			setReloadCell(`${idCel}street`);
		}, 10);
		setData(newData);
		reset();
		setIsNewInput(false);
		setIsSelect(true);
	}, [idCel, reset, rowData, setData, setLoadingChangePoint, setReloadCell]);

	useEffect(() => {
		if (inputValue && !isNewInput && !isSelect) {
			setIsSelect(false);
			setIsNewInput(true);
		}
		if (inputValue && isNewInput && isSelect) {
			setIsNewInput(false);
		}
		if (isSelect) {
			// setResult({ loading: true });
			resetSearchStreet();
		}
	}, [inputValue, isNewInput, isSelect, resetSearchStreet]);

	useEffect(() => {
		if (inputValue === "") {
			updatedDataClear();
		}
	}, [
		inputValue,
		updatedDataClear,
		rowData.raw.street,
		idCel,
		setReloadCell,
		addStreet,
	]);

	useEffect(() => {
		if (
			inputValue &&
			inputValue?.length === 1 &&
			rowData.raw.street?.length >= 1
		) {
			// setResult({ loading: true });
			resetSearchStreet();
			addStreet(undefined, idCel);
			setTimeout(() => {
				setReloadCell(`${idCel}street`);
			}, 10);
		}
	}, [
		resetSearchStreet,
		inputValue,
		rowData.raw.street,
		idCel,
		setReloadCell,
		addStreet,
		updatedDataClear,
	]);

	useEffect(() => {
		if (reloadCell === `${idCel}settlement`) {
			// setResult({ loading: true });
			resetSearchStreet();
			setReloadCell("");
			setInputValue(null);
			reset();
			setSave(false);
			setOpen(false);
			setIsNewInput(false);
			setIsQuery(false);
		}
	}, [
		resetSearchStreet,
		reloadCell,
		idCel,
		setReloadCell,
		setInputValue,
		reset,
		setSave,
		setOpen,
	]);

	const [debouncedQuery] = useDebounce(querySearch, inputRequestDelayMs);

	const mapSearchOptions = useMemo<MapSearch.Options>(() => {
		const baseSearch: StreetBase.Props["searchTypes"] = searchTypes.length
			? searchTypes
			: ["street"];
		return {
			language,
			customerId,
			searchType: customerId ? ["favorite", ...baseSearch] : baseSearch,
			country: "ua",
			query: debouncedQuery,
			near: point ? { point } : undefined,
			responseOrder: [
				"address",
				"street",
				"localObject",
				"object",
				"settlementDistrict",
				"settlement",
				"district",
				"region",
				"adminPlace",
				"adminLevel3",
				"road",
				"country",
			],
			responseOrderBySettlement: true,
			taxiServiceId,
			lite: true,
			mainSettlement: rowData?.raw?.settlement || "",
		};
	}, [
		customerId,
		debouncedQuery,
		language,
		point,
		searchTypes,
		taxiServiceId,
		rowData?.raw?.settlement,
	]);

	const getMap = useCallback(async () => {
		const opt = {
			customerId: mapSearchOptions?.customerId,
			searchType: mapSearchOptions?.searchType,
			query: mapSearchOptions?.query,
			near: mapSearchOptions?.near,
			responseOrder: mapSearchOptions?.responseOrder,
			responseOrderBySettlement:
				mapSearchOptions?.responseOrderBySettlement,
			taxiServiceId: mapSearchOptions?.taxiServiceId,
			lite: mapSearchOptions?.lite,
			mainSettlement: mapSearchOptions?.mainSettlement,
			lang: mapSearchOptions?.language,
			row: idCel || 0,
			orderId,
		};
		updateOptions(opt);
	}, [
		mapSearchOptions?.customerId,
		mapSearchOptions?.language,
		mapSearchOptions?.lite,
		mapSearchOptions?.mainSettlement,
		mapSearchOptions?.near,
		mapSearchOptions?.query,
		mapSearchOptions?.responseOrder,
		mapSearchOptions?.responseOrderBySettlement,
		mapSearchOptions?.searchType,

		mapSearchOptions?.taxiServiceId,
		idCel,
		orderId,
		updateOptions,
	]);

	// const mapSearchResult = useMapSearch(mapSearchOptions);

	const debounceIsQuery = useDebouncedCallback((load: boolean) => {
		setIsQuery(load);
	}, inputRequestDelayMs);

	useEffect(() => {
		if (isString(debouncedQuery) && debouncedQuery?.length >= 3) {
			setIsQuery(false);
			getMap();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedQuery]);

	useEffect(() => {
		if (isString(debouncedQuery) && debouncedQuery?.length >= 3) {
			getMap();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapSearchOptions?.searchType]);

	const responseLength = useMemo(
		() => !!response?.length,
		[response?.length],
	);

	useEffect(() => {
		if (loading) {
			if (inputValue) setLoading(true);
			return;
		}
		if (responseLength) setLoading(false);
	}, [
		responseLength,
		setLoading,
		inputValue,
		debounceIsQuery,
		setOpen,
		loading,
	]);

	useEffect(() => {
		if (!loading && responseLength) {
			setOpen(true);
			debounceIsQuery(false);
		}
	}, [responseLength, loading, debounceIsQuery, setOpen]);

	useEffect(() => {
		if (loading) return;
		if (!loading) setActiveOption(-1);
	}, [loading]);

	useEffect(() => {
		const listBase: boolean[] = [
			// eslint-disable-next-line regex/invalid
			inputValue === "центр",
			// eslint-disable-next-line regex/invalid
			inputValue === "центр",
			inputValue === "center",
		];

		if (inputValue && listBase.includes(true)) {
			if (!loading) {
				const item: Map.Search.Object = {
					...rowData.raw,
					id: "query",
					type: "object",
					language,
					bbox: [],
					geometry: settlement?.geometry || {
						type: "Point",
						coordinates:
							settlement?.coordinates || rowData.coordinates,
					},
					coordinates: settlement?.coordinates || rowData.coordinates,
					street: inputValue,
					country: "",
					countryCode: "",
					entrance: "",
					house: "",
					customHouse: "",
					customStreet: "",
					name: "",
					sector: undefined,
					region: "",
					streetType: "",
					district: "",
					houses: [],
				};

				if (responseLength) {
					const exist = response.find(
						(item) => item.street === inputValue,
					);
					if (!exist) {
						response.unshift(item);
					}
				} else {
					response.push(item);
					debounceIsQuery(false);
				}
			}
		}
	}, [
		response,
		responseLength,
		loading,
		debounceIsQuery,
		inputValue,
		language,
		rowData.coordinates,
		rowData.raw,
		settlement?.coordinates,
		settlement?.geometry,
	]);

	// useEffect(() => {
	// 	if (result?.loading) {
	// 		if (inputValue) setLoading(true);
	// 		return;
	// 	}
	// 	if (result?.objects?.length) {
	// 		setLoading(false);
	// 	}
	// }, [result, setLoading, inputValue, debounceIsQuery, setOpen]);

	// useEffect(() => {
	// 	if (!result?.loading && result?.objects?.length) {
	// 		setOpen(true);
	// 		debounceIsQuery(false);
	// 	}
	// }, [result, debounceIsQuery, setOpen]);

	// useEffect(() => {
	// 	if (mapSearchResult.loading) return;
	// 	// if (mapSearchResult === result) return;
	// 	setResult(mapSearchResult);
	// }, [mapSearchResult, result]);

	// useEffect(() => {
	// 	if (result.loading) return;
	// 	// if (result.objects?.length) setActiveOption(0);
	// 	if (Array.isArray(result?.objects)) setActiveOption(-1);
	// }, [result]);

	// useEffect(() => {
	// 	const listBase: boolean[] = [
	// 		// eslint-disable-next-line regex/invalid
	// 		inputValue === "центр",
	// 		// eslint-disable-next-line regex/invalid
	// 		inputValue === "центр",
	// 		inputValue === "center",
	// 	];

	// 	if (inputValue && listBase.includes(true)) {
	// 		if (!result.loading && Array.isArray(result.objects)) {
	// 			const item: Map.Search.Object = {
	// 				...rowData.raw,
	// 				id: "query",
	// 				type: "object",
	// 				language,
	// 				bbox: [],
	// 				geometry: settlement?.geometry || {
	// 					type: "Point",
	// 					coordinates:
	// 						settlement?.coordinates || rowData.coordinates,
	// 				},
	// 				coordinates: settlement?.coordinates || rowData.coordinates,
	// 				street: inputValue,
	// 				country: "",
	// 				countryCode: "",
	// 				entrance: "",
	// 				house: "",
	// 				customHouse: "",
	// 				customStreet: "",
	// 				name: "",
	// 				sector: undefined,
	// 				region: "",
	// 				streetType: "",
	// 				district: "",
	// 				houses: [],
	// 			};

	// 			if (result.objects.length) {
	// 				const exist = result.objects.find(
	// 					(item) => item.street === inputValue,
	// 				);
	// 				if (!exist) {
	// 					result.objects.unshift(item);
	// 				}
	// 			} else {
	// 				// result.objects.push(item);
	// 				result.objects.push(item);
	// 				debounceIsQuery(false);
	// 			}
	// 		}
	// 	}
	// }, [
	// 	debounceIsQuery,
	// 	inputValue,
	// 	language,
	// 	// result,
	// 	response,
	// 	loading,
	// 	rowData.coordinates,
	// 	rowData.raw,
	// 	settlement?.coordinates,
	// 	settlement?.geometry,
	// ]);

	const onSelect = useCallback(
		async (option: Option<Map.Search.Object>) => {
			if (option) {
				setIsSelect(true);
				setLoading(false);
				if (option?.key === "query") {
					updatedDataAsCity(option.value?.street || "");
				} else {
					let params = option.value;
					const streetBase =
						params.street || params.customStreet || "";
					const houseToken = String(
						params.number || params.house || "",
					).trim();
					const streetQuery =
						houseToken && params.type === "address"
							? `${streetBase}, ${houseToken}`.trim()
							: streetBase;
					if (
						streetQuery &&
						params.coordinates &&
						((params.placeId &&
							params.searchType === "google_geocode") ||
							params.searchType === "osm_nominatim")
					) {
						const enriched = await getStreetCoordinates(
							params as any,
							streetQuery,
						);
						if (enriched) params = enriched;
					}
					updatedData(params);
				}

				setIsNewInput(false);
				// setResult({ loading: true });
				resetSearchStreet();
			}
		},
		[
			getStreetCoordinates,
			setLoading,
			updatedData,
			updatedDataAsCity,
			resetSearchStreet,
		],
	);

	const openOverlay = useMemo(() => open, [open]);

	const placeholder = useMemo(() => {
		if (rowIndex === 0) return pointOnMapFrom;
		return pointOnMapTo;
	}, [pointOnMapFrom, pointOnMapTo, rowIndex]);

	const streetText = useMemo(() => {
		const text = streetShow({
			...rowData.raw,
			isPlaceholder: true,
			baseSave: rowData.save,
		});

		const isPointOnMapText =
			text === "isPlaceholder" && !rowData.save && !rowData.raw.save;
		const textDistrict = isPointOnMapText
			? `${pointOnMap}${
					rowData?.raw?.district ? ` (${rowData?.raw?.district})` : ""
			  }`
			: placeholder;

		let valueText = text;
		if (text === "isPlaceholder") {
			valueText = isPointOnMapText ? textDistrict : "";
		}

		return {
			placeholder:
				text === "isPlaceholder" ? textDistrict : text || placeholder,
			valueText,
		};
	}, [streetShow, rowData.raw, rowData.save, pointOnMap, placeholder]);

	const propsInput = useMemo(
		() => ({
			placeholder: streetText.placeholder,
			value: inputValue !== null ? inputValue : streetText.valueText,
		}),
		[inputValue, streetText],
	);

	const isDisabled = useMemo(() => {
		const list: boolean[] = [
			rowData?.raw?.settlement === pointOnMap,
			!rowData?.raw?.settlement,
		];

		return list.includes(true);
	}, [pointOnMap, rowData?.raw?.settlement]);

	const size = useMemo(
		() =>
			propsInput.value.length > 10 ? propsInput.value.length * 1.25 : 10,
		[propsInput.value.length],
	);

	return (
		<StyledPopup
			useClickControl
			useArrow={false}
			open={openOverlay}
			offset={offset}
			styles={styles}
			trackerId={dataKey + idCel}
			containerId={containerId}
			onChangeOpen={setOpen}
			tracker={
				<StyledInput
					size={size}
					height={height}
					b={isNewInput ? "1px solid red" : ""}
					id={`${idCel + dataKey}`}
					aria-label={dataKey}
					aria-rowindex={rowIndex}
					aria-colindex={colIndex}
					autoComplete="one-time-code"
					onChange={(event) => {
						if (isDisabled) return;
						debounceIsQuery(true);
						setIsNewInput(true);
						onChangeInputValue(event);
						setIsSelect(false);
						setIsQuery(true);
					}}
					onKeyUp={(event) => {
						const keyEvent = event.key;

						if (
							activeId === -1 &&
							keyEvent === "Enter" &&
							!loading
							// !mapSearchResult?.loading
						) {
							return;
						}

						if (isQuery) {
							const list = [
								"ArrowLeft",
								"ArrowRight",
								"Tab",
								"Enter",
							];
							if (list.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}

						if (keyEvent === "Enter" || keyEvent === "Tab") {
							const target = event.target as HTMLInputElement;
							target?.select();
						}

						onEnterToInput(event);
					}}
					onKeyDown={(event) => {
						const keyEvent = event.key;
						const { target } = event;
						const { value } = target as unknown as any;

						const keyList = [
							keyEvent === "Enter",
							keyEvent === "Tab",
						];
						if (
							!value &&
							keyList.includes(true) &&
							rowData.save &&
							!rowData.raw.save
						) {
							event.stopPropagation();
							event.preventDefault();
							return;
						}

						if (isQuery) {
							const list = [
								"ArrowLeft",
								"ArrowRight",
								"Tab",
								"Enter",
							];
							if (list.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}

						// if (activeId === -1 && !mapSearchResult?.loading) {
						// 	return;
						// }

						if (activeId === -1 && !loading) {
							return;
						}
						if (
							activeId >= 0 &&
							!loading &&
							// !mapSearchResult?.loading &&
							!isSelect
						) {
							const list = ["ArrowLeft", "ArrowRight"];
							if (list.includes(keyEvent)) {
								return;
							}
						}

						if (loading && !responseLength) {
							const list = ["ArrowDown", "ArrowUp"];
							if (list.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}
						// if (mapSearchResult?.loading) {
						// 	const list = ["ArrowDown", "ArrowUp"];
						// 	if (list.includes(keyEvent)) {
						// 		event.stopPropagation();
						// 		event.preventDefault();
						// 		return;
						// 	}
						// }

						if (
							keyList.includes(true) &&
							loading
							// mapSearchResult?.loading
						) {
							nextFocus();
							event.stopPropagation();
							event.preventDefault();
							return;
						}

						onExistFromInput(event);
					}}
					onClick={onClickToInput}
					onFocus={(event) => {
						onFocusToInput(event);
					}}
					onCut={(event) => {
						event.preventDefault();
					}}
					onDragOver={(event) => {
						event.preventDefault();
					}}
					onDrop={(event) => {
						event.preventDefault();
					}}
					onBlurCapture={onBlurCaptureToInput}
					{...propsInput}
				/>
			}
		>
			<OverlayStreet
				data={
					isSelect
						? {
								loading: true,
						  }
						: {
								loading: false,
								objects: response,
								resolution: "visicom",
						  }
				}
				activeId={activeId}
				setActiveOption={setActiveOption}
				onSelect={onSelect}
				settlement={settlement?.settlement || rowData.raw.settlement}
				query={inputValue || ""}
			/>
		</StyledPopup>
	);
};

declare namespace StreetBase {
	interface Props extends PropsWithChildren {
		rowData: PointOrderModalTableBase.OrderModal;
		setData: (data: PointOrderModalTableBase.OrderModal) => void;
		/** Add a street to selection houses */
		addStreet: (street: Map.Search.Object | undefined, id: number) => void;
		handleChangeStreet: (id: number) => void;
		addSettlement: (
			settlement: Map.Search.Object | undefined,
			id: number,
		) => void;
		addSettlementCoordinates: (
			settlement: LatLngLiteral | undefined,
			id: number,
		) => void;
		settlementCoordinates?: LatLngLiteral | undefined;
		/** Settlement with streets to selection */
		settlement?: Map.Search.Object;
		street?: Map.Search.Object;
		reset?: (id: number) => void;
		rowIndex?: number;
		dataKey: ValueAccessors | string;
		colIndex: number;

		styles?: Popup.Styles;
		offset?: Popup.Props["offset"];
		height?: number;
		customerId?: number;
		orderId?: number;
		handleEventsOptions?: HandleEventsProps;
		containerId?: string;
		searchTypes?: Map.Search.Type[];
		taxiServiceId?: number;
		countRow?: number;
		reloadCell: string;
		setReloadCell: React.Dispatch<React.SetStateAction<string>>;
		loadingChangePoint: boolean;
		setLoadingChangePoint: React.Dispatch<React.SetStateAction<boolean>>;
	}
}

export const Street = memo(StreetBase);

export default StreetBase;
